import React from 'react';
import PropTypes from 'prop-types';

import { lowBatteryPercent } from '../constants';
import { T, INDICATOR, INDICATOR_TEXT, FONT_MONO } from '../constants/tokens';

/**
 * A dot and a mono label — the v7 way of showing device state.
 *
 * Replaces the 40px filled circle icons the widgets used to stack. At the size
 * these appear in a list row, a coloured dot carries the same signal and lets
 * the label do the talking.
 */
export default function StatusChip({ tone = 'neutral', label, style = {} }) {
  const dot = {
    good: INDICATOR.success,
    bad: INDICATOR.error,
    warn: INDICATOR.warning,
    neutral: T.mutedSoft
  }[tone] ?? T.mutedSoft;

  const ink = {
    good: INDICATOR_TEXT.success,
    bad: INDICATOR_TEXT.error,
    warn: INDICATOR_TEXT.warning,
    neutral: T.muted
  }[tone] ?? T.muted;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: FONT_MONO,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        color: ink,
        whiteSpace: 'nowrap',
        ...style
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 9999, background: dot, flex: 'none' }} />
      {label}
    </span>
  );
}

StatusChip.propTypes = {
  tone: PropTypes.oneOf(['good', 'bad', 'warn', 'neutral']),
  label: PropTypes.node.isRequired,
  style: PropTypes.object
};

/**
 * Connection and battery as a single chip, shared by the badge and hub
 * widgets so the two cannot describe the same state differently.
 *
 * @param {object} device anything with getIsConnected() and getBatteryLevel()
 */
export function connectionChip(device) {
  if (device == null) return null;

  const connected = device.getIsConnected();
  const battery = device.getBatteryLevel();

  if (!connected) return { tone: 'bad', label: 'Not connected' };

  if (battery != null) {
    const percent = Math.floor(battery);
    return {
      tone: percent < lowBatteryPercent() ? 'warn' : 'good',
      label: 'Connected · ' + percent + '%'
    };
  }

  return { tone: 'good', label: 'Connected' };
}

const TONE_COLOR = { good: INDICATOR.success, warn: INDICATOR.warning, bad: INDICATOR.error };

/**
 * The colour for a device card's edge bar.
 *
 * The device's own health, in the colours of its chip: green connected, amber
 * low on power, red not connected. A room that is in an alert takes over,
 * since that matters more than the state of the device in it.
 *
 * The bar used to be the room's status colour alone, and Normal is grey, so a
 * healthy badge in a quiet school always had a grey bar.
 *
 * @param {object | null | undefined} device anything connectionChip accepts
 * @param {import('../lib').AlertType | null | undefined} roomStatus the alert status of the room it is in
 */
export function deviceAccent(device, roomStatus) {
  const name = roomStatus?.getDashboardStatus?.();
  if (name != null && name !== 'Normal') return roomStatus.getColor()?.main;
  const chip = connectionChip(device);
  return chip ? TONE_COLOR[chip.tone] : undefined;
}
